import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PagePayload } from '@core/interface/core';
import {
  PropertyResponse,
  Property,
  AddPropertyResponse,
  PropertyCommonResponse,
  PropertyExportStatusResponse,
} from '@core/interface/property';
import { environment } from '@environment/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PropertyService {
  constructor(private http: HttpClient) {}

  getProperties(filter?: PagePayload) {
    return this.http.get<PropertyResponse>(environment.endPoints.propertyList, {
      params: this.buildParams(filter),
    });
  }

  /**
   * Server-side property export.
   * - Sync (<=5000): 200 + xlsx blob
   * - Async (>5000 or async=1): 202 JSON — emailed to user (no browser download)
   */
  exportProperties(filter?: PagePayload): Observable<HttpResponse<Blob>> {
    return this.http.get(environment.endPoints.propertyExport, {
      params: this.buildParams(filter),
      responseType: 'blob',
      observe: 'response',
    });
  }

  /** Poll GET /property/export/status?file=... */
  getExportStatus(
    fileName: string
  ): Observable<PropertyExportStatusResponse> {
    return this.http.get<PropertyExportStatusResponse>(
      environment.endPoints.propertyExportStatus,
      { params: { file: fileName } }
    );
  }

  /** Poll via absolute/relative status_url from 202 response. */
  getExportStatusByUrl(
    statusUrl: string
  ): Observable<PropertyExportStatusResponse> {
    return this.http.get<PropertyExportStatusResponse>(
      this.resolveUrl(statusUrl)
    );
  }

  /** Download ready export file (usually .csv for large exports). */
  downloadExportFile(downloadUrl: string): Observable<Blob> {
    return this.http.get(this.resolveUrl(downloadUrl), {
      responseType: 'blob',
    });
  }

  addProperty(data: Property) {
    return this.http.post<AddPropertyResponse>(
      environment.endPoints.propertyAddEdit,
      data
    );
  }

  deleteProperty(id: number) {
    return this.http.get<PropertyCommonResponse>(
      environment.endPoints.propertyDelete(id)
    );
  }

  resolveUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    const base = environment.baseURL.replace(/\/$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;

    // status/download URLs may be absolute from app root (/api/...) or API-relative
    if (path.startsWith('/api/')) {
      const origin = base.replace(/\/api$/, '');
      return `${origin}${path}`;
    }

    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private buildParams(filter?: PagePayload): HttpParams {
    let httpParams = new HttpParams();
    if (!filter) {
      return httpParams;
    }

    Object.keys(filter).forEach((key) => {
      const value = (filter as Record<string, unknown>)[key];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }
}
